import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Typography } from '@mui/material';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { toMeta } from '#helper/rhf-meta';
import SelectProject from '#component/form/multiple-select-project';
import { isEnabledModuleById } from '#component/project/form/helper';
import { fetchSelectDirectProjects, clearSelectProjects } from '#action/project/project-select';
import validate from './validate';
import {
  ProjectFormContainer,
  StyledPaper,
  ProjectField,
  ButtonContainer,
  FormButton
} from './styles';
import { isProjectExpired } from '#helper';

/**
 * Renders addressbook react-hook-form fields.
 * @returns {React.FC}
 */

const Form = props => {
  const {
    pageDescription,
    history,
    addressbook,
    initialValues,
    onSubmit
  } = props;
  const dispatch = useDispatch();
  const ADDRESS_BOOK_MODULE_ID = 4;
  const [hasProject, setHasProject] = useState(addressbook?.project ?? false);
  const { status } = useSelector(s => s.projectSelect, shallowEqual);
  const { directProjects } = useSelector(s => s.account, shallowEqual);

  const methods = useForm({
    resolver: reduxFormResolver(validate),
    defaultValues: initialValues || {}
  });

  const { control, handleSubmit, formState, reset } = methods;
  const { isDirty, isSubmitting } = formState;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const directlyAssignedProjects = directProjects?.filter(
    project => !isProjectExpired(project)
  );
  const projectsToSelect = useMemo(() => directlyAssignedProjects.map(project => ({
    label: project.title,
    value: project.id,
    disabled: !isEnabledModuleById(project.modules, ADDRESS_BOOK_MODULE_ID),
    disabledMsg: 'Enable module',
    ...project
  })), [directlyAssignedProjects]);

  const handleCancelClick = () => {
    history.push('/addressbook');
  };

  const handleProjectsFetch = e => dispatch(fetchSelectDirectProjects(e.target.value));

  const clearProjects = () => {
    dispatch(clearSelectProjects());
  };

  const fetchProjects = () => {
    dispatch(fetchSelectDirectProjects());
  };

  return (
    <FormProvider {...methods}>
      <ProjectFormContainer
        component="form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography variant="h4" gutterBottom>
          {pageDescription}
        </Typography>

        <StyledPaper elevation={0}>
          <RHFTextField
            name="label"
            label="Label"
            variant="outlined"
            fullWidth
          />
          <ProjectField mobile={hasProject ? 1 : 0}>
            <Controller
              name="projectId"
              control={control}
              render={({ field, fieldState }) => (
                <SelectProject
                  meta={toMeta(fieldState, formState)}
                  label="Project"
                  placeholder="Project"
                  data={projectsToSelect}
                  setData={project => {
                    field.onChange(project.value);
                    setHasProject(true);
                  }}
                  defaultData={addressbook?.project ?? []}
                  fetchProjects={handleProjectsFetch}
                  clearProjects={clearProjects}
                  loading={['loading'].includes(status)}
                  textFieldOnFocus={fetchProjects}
                  showDateRange
                />
              )}
            />
          </ProjectField>
        </StyledPaper>
        <ButtonContainer>
          <FormButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </FormButton>
          <FormButton
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!isDirty || isSubmitting}
          >
            Save
          </FormButton>
        </ButtonContainer>
      </ProjectFormContainer>
    </FormProvider>
  );
};

export default Form;
